import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { companyInfo } from '../utils/mock';

const Hero = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Hero Section with Beautiful Truck Background */}
      <section id="hero" className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Truck with Trailer Background */}
        <div className="absolute inset-0 z-0">
          <div 
            className="absolute inset-0 bg-cover bg-center scale-105"
            style={{ 
              backgroundImage: 'url(/hero-truck-trailer.jpg)',
              transform: `translateY(${scrollY * 0.3}px) scale(1.05)`,
              transition: 'transform 0.1s ease-out',
              filter: 'brightness(0.7) contrast(1.1)'
            }}
          ></div>
          
          {/* Strong Dark Overlay for Perfect Text Readability */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#0D2436]/90 via-[#1B4B6B]/85 to-[#0D2436]/92"></div>
          
          {/* Vignette Effect */}
          <div className="absolute inset-0" style={{
            background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%)'
          }}></div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 relative z-10 text-center">
          <div className="max-w-5xl mx-auto">
            {/* Company Name with Elegant Typography */}
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight animate-fade-in-up drop-shadow-2xl tracking-tight">
              {companyInfo.name}
            </h1>

            {/* Tagline with Style */}
            <div className="relative inline-block mb-12">
              <p className="text-xl md:text-3xl text-white font-light animate-fade-in-up animation-delay-200 drop-shadow-lg tracking-wide">
                {companyInfo.tagline}
              </p>
              <div className="absolute -bottom-3 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-[#E6A05C] to-transparent"></div>
            </div>

            {/* Elegant CTA Button */}
            <div className="mt-12 animate-fade-in-up animation-delay-400">
              <button 
                onClick={scrollToContact}
                className="group relative bg-gradient-to-r from-[#E6A05C] via-[#F0B572] to-[#E6A05C] text-white px-12 py-5 rounded-full font-bold text-xl hover:shadow-2xl transition-all duration-500 flex items-center space-x-3 mx-auto border-2 border-white/30 hover:border-white/50 overflow-hidden"
                style={{
                  backgroundSize: '200% auto'
                }}
              >
                <span className="relative z-10">Get Your Free Quote</span>
                <ArrowRight className="relative z-10 group-hover:translate-x-2 transition-transform duration-300" size={24} />
                <div className="absolute inset-0 bg-gradient-to-r from-[#F0B572] via-[#E6A05C] to-[#F0B572] opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{
                  backgroundSize: '200% auto',
                  animation: 'shimmer 3s linear infinite'
                }}></div>
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-16 flex items-center justify-center space-x-8 animate-fade-in-up animation-delay-600">
              <div className="text-center">
                <div className="text-[#E6A05C] text-sm font-semibold uppercase tracking-wider mb-1">Trusted Since</div>
                <div className="text-white text-2xl font-bold">{companyInfo.established}</div>
              </div>
              <div className="h-12 w-px bg-white/30"></div>
              <div className="text-center">
                <div className="text-[#E6A05C] text-sm font-semibold uppercase tracking-wider mb-1">On-Time Rate</div>
                <div className="text-white text-2xl font-bold">98%</div>
              </div>
              <div className="h-12 w-px bg-white/30"></div>
              <div className="text-center hidden md:block">
                <div className="text-[#E6A05C] text-sm font-semibold uppercase tracking-wider mb-1">Available</div>
                <div className="text-white text-2xl font-bold">24/7</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator with Style */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
          <div className="flex flex-col items-center cursor-pointer group" onClick={() => window.scrollBy({ top: window.innerHeight, behavior: 'smooth' })}>
            <div className="w-6 h-10 border-2 border-white/60 rounded-full flex items-start justify-center p-2 group-hover:border-[#E6A05C] transition-colors duration-300">
              <div className="w-1 h-3 bg-[#E6A05C] rounded-full animate-scroll"></div>
            </div>
            <span className="text-white/80 text-xs mt-2 font-medium uppercase tracking-widest">Scroll</span>
          </div>
        </div>
      </section>

      {/* Stats Section with Premium Design */}
      <section className="py-20 md:py-28 bg-gradient-to-br from-white via-[#FFFBF7] to-[#FFF8F0] relative overflow-hidden">
        {/* Elegant Background */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full" style={{
            backgroundImage: `radial-gradient(circle at 20% 50%, rgba(230, 160, 92, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(212, 137, 75, 0.1) 0%, transparent 50%)`,
          }}></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          {/* Stats Grid with Premium Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            <div className="group bg-white backdrop-blur-sm border-2 border-[#E6A05C]/20 rounded-2xl p-8 hover:border-[#E6A05C] hover:shadow-2xl transition-all duration-500 hover:scale-105 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#E6A05C]/5 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-[#E6A05C] to-[#D4894B] bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300 relative z-10">1K+</div>
              <div className="text-sm md:text-base text-gray-700 font-semibold relative z-10">Monthly Deliveries</div>
            </div>
            <div className="group bg-white backdrop-blur-sm border-2 border-[#E6A05C]/20 rounded-2xl p-8 hover:border-[#E6A05C] hover:shadow-2xl transition-all duration-500 hover:scale-105 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#E6A05C]/5 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-[#E6A05C] to-[#D4894B] bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300 relative z-10">98%</div>
              <div className="text-sm md:text-base text-gray-700 font-semibold relative z-10">On-Time Delivery</div>
            </div>
            <div className="group bg-white backdrop-blur-sm border-2 border-[#E6A05C]/20 rounded-2xl p-8 hover:border-[#E6A05C] hover:shadow-2xl transition-all duration-500 hover:scale-105 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#E6A05C]/5 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-[#E6A05C] to-[#D4894B] bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300 relative z-10">24/7</div>
              <div className="text-sm md:text-base text-gray-700 font-semibold relative z-10">Customer Support</div>
            </div>
            <div className="group bg-white backdrop-blur-sm border-2 border-[#E6A05C]/20 rounded-2xl p-8 hover:border-[#E6A05C] hover:shadow-2xl transition-all duration-500 hover:scale-105 text-center relative overflow-hidden">
              <div className="absolute top-0 right-0 w-20 h-20 bg-[#E6A05C]/5 rounded-full transform translate-x-10 -translate-y-10 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="text-5xl md:text-6xl font-bold bg-gradient-to-br from-[#E6A05C] to-[#D4894B] bg-clip-text text-transparent mb-3 group-hover:scale-110 transition-transform duration-300 relative z-10">15+</div>
              <div className="text-sm md:text-base text-gray-700 font-semibold relative z-10">Fleet Vehicles</div>
            </div>
          </div>

          {/* Description with Style */}
          <div className="text-center mt-16 max-w-3xl mx-auto">
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed font-light">
              From freight and long haul to cross-border logistics, we deliver <span className="text-[#E6A05C] font-semibold">reliability, safety, and excellence</span> across North America.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default Hero;
